import { localeFromPath, stripLocale, type Locale } from '@/i18n/locale'
import { SECTION_PATHS, type SectionSlug } from './section-paths'

export { SECTION_PATHS, type SectionSlug }

/**
 * Адреса блоков главной: короткий путь → id блока.
 *
 * Это та же главная, открытая на нужном месте: /roi показывает всю страницу,
 * прокрученную к калькулятору. Пути — настоящие маршруты (routes.tsx), а не
 * редиректы на «#якорь», чтобы адрес можно было скопировать из строки
 * браузера после клика по меню. Для поисковика это одна страница: canonical
 * у всех этих путей указывает на главную (Seo.tsx, SeoLocale.tsx).
 *
 * Сами пары «путь → блок» лежат в section-paths.ts (единственный источник);
 * здесь — логика вокруг них. Слаги повторяют пункты меню.
 */
const SLUG_BY_ANCHOR = Object.fromEntries(
  Object.entries(SECTION_PATHS).map(([slug, anchor]) => [anchor, slug]),
) as Record<string, SectionSlug>

/** id блока для адреса вида /roi или /en/roi; null — если это не адрес блока. */
export function sectionFromPath(pathname: string): string | null {
  const slug = stripLocale(pathname).replace(/^\/|\/$/g, '')
  return (SECTION_PATHS as Record<string, string>)[slug] ?? null
}

/** Главная в любом виде: «/», «/en» или адрес её блока. */
export function isHomePath(pathname: string): boolean {
  const base = stripLocale(pathname)
  return base === '/' || sectionFromPath(pathname) !== null
}

/** Адрес самой главной для локали текущего пути: «/» или «/en». */
export function homeBase(pathname: string): string {
  return localeFromPath(pathname) === 'en' ? '/en' : '/'
}

/** Короткий путь для якоря: ('calculator', 'en') → '/en/roi'; null — адреса у блока нет. */
export function pathForAnchor(anchor: string, locale: Locale): string | null {
  const slug = SLUG_BY_ANCHOR[anchor]
  if (!slug) return null
  return locale === 'en' ? `/en/${slug}` : `/${slug}`
}

/**
 * Путь, который считается «страницей» для canonical, hreflang и переключателя
 * языка: адрес блока сворачивается в главную, всё остальное — как есть.
 */
export function canonicalPath(pathname: string): string {
  return sectionFromPath(pathname) ? homeBase(pathname) : pathname
}
